/**
 * Units that cannot be targeted by single-unit strategems (area-effect strategems may still hit them).
 * Confused units lose immunity and can be targeted.
 */

const STRATEGEM_IMMUNE_ALL_ORDER_IDS = new Set([96, 262]);
const STRATEGEM_IMMUNE_ENEMY_ORDER_IDS = new Set([384]);

export function unitOrderId(unit) {
  if (!unit) return null;
  const raw = unit.id ?? unit.orderId;
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

export function isUnitConfused(unit) {
  return !!(unit?.confused || unit?.isConfused);
}

/**
 * @param {Object} unit
 * @param {Object} [options]
 * @param {string} [options.castingPlayerId]
 * @param {string} [options.unitOwnerPlayerId]
 * @param {'friendly'|'opponent'|'unknown'} [options.unitAlignment] - unit relative to the caster
 */
export function isUnitUntargetableByStrategem(unit, options = {}) {
  if (!unit || isUnitConfused(unit)) return false;

  const orderId = unitOrderId(unit);
  const specialAbility = unit.specialAbility;

  if (specialAbility === 'immune_to_strategems' || (orderId != null && STRATEGEM_IMMUNE_ALL_ORDER_IDS.has(orderId))) {
    return true;
  }

  const hasEnemyOnlyImmunity =
    specialAbility === 'immune_to_enemy_strategems' ||
    (orderId != null && STRATEGEM_IMMUNE_ENEMY_ORDER_IDS.has(orderId));

  if (!hasEnemyOnlyImmunity) return false;

  const { castingPlayerId, unitOwnerPlayerId, unitAlignment } = options;
  if (unitAlignment === 'opponent') return true;
  if (unitAlignment === 'friendly') return false;
  if (castingPlayerId != null && unitOwnerPlayerId != null) {
    return String(castingPlayerId) !== String(unitOwnerPlayerId);
  }
  return false;
}

export function getStrategemTargetingBlockReason(unit) {
  if (!unit || isUnitConfused(unit)) return null;
  const orderId = unitOrderId(unit);
  const name = unit.name || (orderId != null ? `Unit ${orderId}` : 'Unit');
  if (orderId === 96) return `${name} cannot be targeted by strategems`;
  if (orderId === 262) return `${name} cannot be targeted by strategems`;
  if (orderId === 384) return `${name} cannot be targeted by enemy strategems`;
  if (unit.specialAbility === 'immune_to_strategems') return `${name} cannot be targeted by strategems`;
  if (unit.specialAbility === 'immune_to_enemy_strategems') return `${name} cannot be targeted by enemy strategems`;
  return `${name} cannot be targeted by strategems`;
}
